<template>
  <div
    class="modal fade"
    :class="{ show: isVisible }"
    tabindex="-1"
    v-if="isVisible"
    :key="`modal-${modalKey}`"
  >
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            Bulk Edit - {{ selectedItems.length }} items
          </h5>
          <button type="button" class="btn-close" @click="close"></button>
        </div>
        <div class="modal-body">
          <div v-if="!selectedField">
            <!-- Paso 1: Seleccionar campo -->
            <div class="mb-4">
              <h6>Step 1: Select the field to edit</h6>
              <p class="text-muted">
                Choose which field you want to modify for all selected items.
              </p>
            </div>

            <div class="row">
              <div class="col-12">
                <label class="form-label">Field to edit:</label>
                <select
                  v-model="selectedFieldKey"
                  class="form-select"
                  @change="onFieldSelected"
                >
                  <option value="">-- Select a field --</option>
                  <option
                    v-for="field in availableFields"
                    :key="field.key"
                    :value="field.key"
                  >
                    {{ field.label || field.key }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <div v-else>
            <!-- Paso 2: Editar valor usando Form.vue -->
            <div class="mb-4">
              <h6>Step 2: Set the new value</h6>
              <p class="text-muted">
                This value will be applied to
                {{ selectedItems.length }} selected items.
              </p>
            </div>

            <div v-if="selectedField && referenceItems && modelName">
              <FormWrapper
                :key="`form-${selectedField.key}-${modalKey}-${Date.now()}`"
                :fields="[selectedField]"
                v-model="formData"
                :lists="referenceItems"
                :visualGroups="[]"
                :selectedModel="modelName"
                :defaultFormData="formData"
                :showButtons="false"
                @input="(val) => (formData = val)"
              />
            </div>

            <!-- Vista previa de cambios -->
            <div class="mt-4">
              <h6>Preview of changes:</h6>
              <div class="alert alert-info">
                <strong>Field:</strong>
                {{ selectedField.label || selectedField.key }}<br />

                <strong>Items to update:</strong> {{ selectedItems.length }}
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="close">
            Cancel
          </button>
          <button
            v-if="selectedField"
            type="button"
            class="btn btn-primary"
            @click="applyChanges"
            :disabled="isSubmitting"
          >
            <span
              v-if="isSubmitting"
              class="spinner-border spinner-border-sm me-2"
              role="status"
            ></span>
            Apply to {{ selectedItems.length }} items
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import Swal from "sweetalert2";
import FormWrapper from "./FormWrapper.vue";

export default {
  name: "BulkEditModal",
  components: {
    FormWrapper,
  },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    selectedItems: {
      type: Array,
      required: true,
    },
    modelFields: {
      type: Array,
      required: true,
    },
    referenceItems: {
      type: [Object, Array],
      default: () => ({}),
    },
    modelName: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      selectedFieldKey: "",
      selectedField: null,
      formData: {},
      isSubmitting: false,
      modalKey: 0,
    };
  },
  computed: {
    availableFields() {
      // Filtrar campos que se pueden editar masivamente
      return this.modelFields.filter((field) => {
        // Excluir campos que no se pueden editar masivamente
        const excludedTypes = ["group", "array"];
        return !excludedTypes.includes(field.type) && !field.readonly;
      });
    },
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.resetForm();
      }
    },
    selectedField: {
      handler(newVal) {
        if (newVal) {
          // Forzar la actualización cuando cambie el campo seleccionado
          this.$nextTick(() => {
            this.$forceUpdate();
          });
        }
      },
      immediate: true,
    },
  },
  methods: {
    resetForm() {
      this.selectedFieldKey = "";
      this.selectedField = null;
      this.formData = {};
      this.isSubmitting = false;
      this.modalKey = 0;
    },

    onFieldSelected() {
      if (!this.selectedFieldKey) {
        this.selectedField = null;
        this.formData = {};
        return;
      }

      this.selectedField = this.modelFields.find(
        (f) => f.key === this.selectedFieldKey
      );

      // Inicializar formData con el campo seleccionado
      if (this.selectedField) {
        this.formData = {};

        // Inicializar valor por defecto según el tipo
        switch (this.selectedField.type) {
          case "boolean":
            this.formData[this.selectedField.key] = false;
            break;
          case "number":
            this.formData[this.selectedField.key] = 0;
            break;
          case "list":
          case "reference":
            this.formData[this.selectedField.key] = null;
            break;
          default:
            this.formData[this.selectedField.key] = "";
        }

        // Forzar re-renderizado completo del modal
        this.modalKey++;

        // Esperar a que el DOM se actualice y luego forzar la actualización del Form
        this.$nextTick(() => {
          setTimeout(() => {
            this.$forceUpdate();
            // También forzar la actualización de todos los componentes hijos
            this.$children.forEach((child) => {
              if (child.$forceUpdate) {
                child.$forceUpdate();
              }
            });
          }, 50);
        });
      }
    },

    getReferenceLabel(referenceId) {
      if (!referenceId) return "(empty)";

      // Buscar en las referencias disponibles
      if (
        this.selectedField.ref &&
        this.referenceItems[this.selectedField.ref]
      ) {
        const reference = this.referenceItems[this.selectedField.ref].find(
          (r) => r._id === referenceId
        );
        return reference ? reference.name || reference.label : referenceId;
      }

      return referenceId;
    },

    getListLabel(listId) {
      if (!listId) return "(empty)";

      // Buscar en las listas disponibles
      if (
        this.selectedField.listType &&
        this.referenceItems[this.selectedField.listType]
      ) {
        const listItem = this.referenceItems[this.selectedField.listType].find(
          (l) => l._id === listId
        );
        return listItem ? listItem.label : listId;
      }

      return listId;
    },

    async applyChanges() {
      if (!this.selectedField) return;

      const result = await Swal.fire({
        title: "Confirm bulk edit",
        text: `Are you sure you want to update ${this.selectedItems.length} items? This action cannot be undone.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#dc3545",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "Yes, apply changes",
        cancelButtonText: "Cancel",
      });

      if (!result.isConfirmed) return;

      this.isSubmitting = true;

      try {
        // Mostrar loading
        Swal.fire({
          title: "Applying changes...",
          text: "Please wait...",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        // Crear el objeto de actualización con solo el campo seleccionado
        const updateData = {
          [this.selectedField.key]: this.formData[this.selectedField.key],
        };

        // Aplicar cambios a todos los items seleccionados usando el nuevo endpoint de campo específico
        const updatePromises = this.selectedItems.map((itemId) =>
          wencesApi.put(
            `/api/gotrading/field/${this.modelName}/${itemId}`,
            updateData
          )
        );

        await Promise.all(updatePromises);

        // Mostrar mensaje de éxito
        await Swal.fire({
          title: "Success!",
          text: `${this.selectedItems.length} items have been updated successfully.`,
          icon: "success",
        });

        // Emitir evento de éxito
        this.$emit("bulk-edit-success");
        this.close();
      } catch (error) {
        console.error("Error applying bulk changes:", error);

        await Swal.fire({
          title: "Error",
          text: "An error occurred while applying the changes. Please try again.",
          icon: "error",
        });
      } finally {
        this.isSubmitting = false;
      }
    },

    close() {
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.modal {
  display: block;
  background-color: rgba(0, 0, 0, 0.5);
}

.modal.show {
  display: block;
}
</style>
